import React from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectLabels } from "../../../slices/language";
import api from "../../../api";
import { showError } from "../../../utils/helpers";
import { Autocomplete, TextField } from "@mui/material";
import IconifyIcon from "../../IconifyIcon";

export interface ProviderInfo {
  key: string;
  id: number;
  label: string;
  img: string;
}

interface NewInvitationProviderDataProps {
  selectedProvider: ProviderInfo | null;
  setSelectedProvider: (selectedProvider: ProviderInfo | null) => void;
  otherProvider: string;
  setOtherProvider: (otherProvider: string) => void;
  isMobile: boolean;
}

const NewInvitationProviderData = ({
  selectedProvider,
  setSelectedProvider,
  otherProvider,
  setOtherProvider,
  isMobile,
}: NewInvitationProviderDataProps) => {
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();

  const [providerLabel, setProviderLabel] = React.useState<string>("");
  const [providers, setProviders] = React.useState<ProviderInfo[]>([]);

  const defaultProviderProps = {
    options: providers ? providers : [],
    getOptionLabel: (option: ProviderInfo) => option.label,
  };

  const getProviders = React.useCallback(async () => {
    try {
      const response = await api.providersRequest();
      if (response) {
        const fixedResponse: ProviderInfo[] = response?.data?.map(
          (item, index) => {
            return {
              key: `${item?.id}-${index}`,
              id: item?.id,
              label: item?.name,
              img: item?.logo_url,
            };
          }
        );
        fixedResponse.push({
          key: "0",
          id: 0,
          label: labels.other,
          img: "",
        });
        setProviders(fixedResponse);
      }
    } catch (error) {
      console.log(error);
      showError(dispatch, labels.generic_error);
    }
  }, [dispatch, labels.generic_error, labels.other]);

  React.useEffect(() => {
    getProviders();
  }, [getProviders]);

  return (
    <div
      className="w-100 border-box hidden flex flex-dir-col align-items-center gap-6 p-8"
      style={{
        backgroundColor: "white",
        border: "1px #e0e0e0 solid",
        borderRadius: 6,
      }}
    >
      <div className="w-100">
        <p className="t-nowrap body-text-lg font-w-500 title-color mb-4">
          {labels.provider}
        </p>
      </div>
      <div
        className={
          isMobile
            ? "w-100 border-box flex flex-dir-col align-items-center gap-6"
            : "w-100 border-box flex align-items-center gap-10"
        }
      >
        <div className={isMobile ? "w-100" : "w-50"}>
          <p className="t-nowrap body-text-xs mb-3">
            {labels.select_provider} *
          </p>
          {/* <Autocomplete
            {...defaultProviderProps}
            autoComplete
            value={selectedProvider}
            onChange={(event: any, newValue: ProviderInfo | null) => {
              setSelectedProvider(newValue);
            }}
            inputValue={providerLabel}
            onInputChange={(event, newInputValue) => {
              setProviderLabel(newInputValue);
            }}
            id="UsersByUnit"
            size="small"
            renderInput={(params) => <TextField {...params} />}
          /> */}
          <Autocomplete
            {...defaultProviderProps}
            autoComplete
            value={selectedProvider}
            onChange={(event: any, newValue: ProviderInfo | null) => {
              setSelectedProvider(newValue);
            }}
            inputValue={providerLabel}
            onInputChange={(event, newInputValue) => {
              setProviderLabel(newInputValue);
            }}
            id="UsersByUnit"
            size="small"
            renderOption={(props, option: ProviderInfo) => (
              <li {...props} key={option.id}>
                <IconifyIcon icon={option.img} style={{ marginRight: 8 }} />
                {option.label}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: selectedProvider ? (
                    <>
                      <IconifyIcon
                        icon={selectedProvider.img}
                        style={{ marginRight: 8 }}
                      />
                      {params.InputProps.startAdornment}
                    </>
                  ) : null,
                }}
              />
            )}
          />
        </div>
        <div className={isMobile ? "w-100" : "w-50"}>
          {selectedProvider?.id === 0 && (
            <>
              <p className="t-nowrap body-text-xs mb-3">
                {labels.provider_name} *
              </p>
              <TextField
                fullWidth
                placeholder={labels.enter_your_name}
                value={otherProvider}
                size="small"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setOtherProvider(event.target.value);
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewInvitationProviderData;
